// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import type {
  Claim,
  ClaimGetAllParams,
  ClaimGetAllResponse,
  ClaimStatus,
  PartialClaim,
  WithId,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import type { ColumnType } from 'components/table';
import type { Field, SelectOption } from 'components/types';
import { claimTypeDisplay, getDisplayDate } from 'core/display';
import { PaginatedTable } from 'core/table';
import { Section, Text } from 'design-system';
import { router } from 'expo-router';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import { ClaimApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

import { ClaimListToggle } from './internal';

const api = new ClaimApi();

const columns: ColumnType<WithId<PartialClaim>>[] = [
  {
    accessor: 'patient',
    header: strings.LabelNameAndSurname,
    Cell: ({ value }) => (
      <Text>{value.display || strings.DisplayNotFound}</Text>
    ),
  } as ColumnType<Claim, 'patient'>,
  {
    accessor: 'type',
    header: strings.AdmissionServiceLabel,
    Cell: ({ value }) => <Text>{claimTypeDisplay[value] || value}</Text>,
  } as ColumnType<WithId<Claim>, 'type'>,
  {
    accessor: 'status',
    header: strings.LabelStatus,
  },
  {
    accessor: 'created',
    header: strings.ClaimLabelDate,
    Cell: ({ value }) => (
      <Text>{getDisplayDate(value) || strings.DisplayNotFound}</Text>
    ),
  } as ColumnType<Claim, 'created'>,
];

type SearchParams = Omit<ClaimGetAllParams, 'pageToken' | 'count'>;

const statuses: ClaimStatus[] = [
  'active',
  'draft',
  'cancelled',
  'entered-in-error',
];

const statusOptions: SelectOption<never, ClaimStatus>[] = statuses.map(
  (status) => ({
    value: status,
    display: status,
  }),
);

const searchFields: Field<SearchParams>[] = [
  {
    subfields: [
      {
        label: strings.LabelPatientName,
        key: 'patientName',
        type: 'text-box',
        placeholder: strings.PlaceHolderSearch,
      },
      {
        label: strings.LabelStatus,
        key: 'status',
        type: 'combobox-single',
        fetcher: statusOptions,
      },
    ],
  },
];

const getDataFromResponse = (res: ClaimGetAllResponse) => res.claims;

export const ClaimListScreen: React.FC = () => {
  const [search, setSearch] = useState<SearchParams>({});

  const fetcher = useCallback(
    (options?: FetcherOptions) =>
      api.getAll({ ...search, pageToken: options?.nextToken, ...options }),
    [search],
  );

  const baseSWRKey = `claim-list-${search.status}-${search.patientName}`;

  const onRowPress = useCallback((row: WithId<PartialClaim>) => {
    const id = row.id;
    if (!id) {
      return;
    }

    // Correct way to navigate with params in Expo Router
    router.push({
      pathname: '/admin/claim/[id]',
      params: { id },
    });
  }, []);

  return (
    <ScrollView>
      <ClaimListToggle active="ListClaims">
        <CrudForm
          hasCard={false}
          fields={searchFields}
          initialValues={{}}
          onChange={setSearch}
          showSubmitButton={false}
        />
      </ClaimListToggle>
      <Section>
        <PaginatedTable
          cacheKey={baseSWRKey}
          fetcher={fetcher}
          onRowPress={onRowPress}
          getDataFromResponse={getDataFromResponse}
          columns={columns}
          canChangeCount
        />
      </Section>
    </ScrollView>
  );
};
